import { Close } from '@casumo/dojo-react-icons';
import classNames from 'classnames';
import React, { memo, MouseEvent, useMemo, useState } from 'react';
import useTermsAndConditions from '../api/hooks/useTermsAndConditions.tsx';
import Drawer from '../design-system/drawer.tsx';
import Skeleton from '../design-system/skeleton.tsx';
import { useRegistrationStore } from '../store/store.ts';
import { TConsentField } from '../types/types.ts';
import { TEST_ID } from '../utils/constants.ts';
import { GtmEvents } from '../utils/gtm/gtm.ts';

const TERMS_UNAVAILABLE = 'These terms could not be loaded right now. Please try again later.';

type TDynamicButtons = {
  [key: string]: {
    text?: string;
    link?: string;
    onClick: (e: MouseEvent<HTMLAnchorElement>) => void;
  };
}

const Anchor = ({ type, text, link, onClick }: {
  type: string;
  text: string;
  link: string;
  onClick: (e: MouseEvent<HTMLAnchorElement>) => void;
}) => {
  const { metadata } = useRegistrationStore();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    GtmEvents.logTerms(type, metadata?.currentStep);
    onClick(e);
  };

  const handleAnchorClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    GtmEvents.logTerms(type, metadata?.currentStep);
  }
  const testID = TEST_ID.PREFIX + type;

  return link
    ? <a
        className="underline"
        href={link} target="_blank"
        rel="noreferrer"
        data-testid={testID}
        onClick={handleAnchorClick}
      >
        {text}
      </a>
    : <a className="underline cursor-pointer" onClick={handleClick} data-testid={testID}>{text}</a>;
};

const parseText = (description: string | null, dynamicButtons: TDynamicButtons) => {
  const regex = /\[\[(.+?)]]/g;

  if (typeof description !== 'string') {
    return null;
  }

  const matches = Array.from(description.matchAll(regex));

  if (matches.length === 0) {
    return description;
  }

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  matches.forEach((match, index) => {
    if (match.index! > lastIndex) {
      result.push(description.slice(lastIndex, match.index));
    }

    result.push(
      <Anchor
        key={index}
        link={dynamicButtons?.[match[1]]?.link || ''}
        text={dynamicButtons?.[match[1]]?.text || ''}
        onClick={dynamicButtons?.[match[1]]?.onClick}
        type={match[1]}
      />,
    );
    lastIndex = match.index! + match[0].length;
  });

  if (lastIndex < description.length) {
    result.push(description.slice(lastIndex));
  }

  return result;
};

const Consent = (
  {
    name,
    title,
    description,
    consents,
    isCheckable,
    textAlign,
    widgetTitle,
  }: TConsentField & { error?: boolean; errorMessage?: string; widgetTitle?: string }) => {
  const { formData, setFormData, errors, setErrors } = useRegistrationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [currentConsent, setCurrentConsent] = useState('');

  const buttons: TDynamicButtons = consents?.map((consent) => ({
    [consent.name]: {
      text:    consent.replaceWith,
      link:    consent.link,
      onClick: (e: MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        setCurrentConsent(consent.name);
        setIsOpen(true);
      },
    },
  })).reduce((acc, curr) => ({ ...acc, ...curr }), {});

  const consent = useMemo(() => consents
    ?.find(consent => consent.name === currentConsent), [consents, currentConsent]);

  const { getByName, loading, error: termsError } = useTermsAndConditions(
    consents?.map(({ staticTermsName }) => staticTermsName) ?? [],
  );

  const terms = consent?.staticTermsName ? getByName(consent.staticTermsName) : undefined;

  const handleCheckboxChange = (e: MouseEvent<HTMLInputElement>) => {
    if (!isCheckable) {
      return
    }
    setFormData({ [name]: e.currentTarget.checked });
    setErrors(e.currentTarget.checked ? { [name]: '' } : { [name]: 'Please accept the terms and conditions' });
  }

  const sanitizedDescription = typeof description === 'string' ? description : '';

  const regex = /\[\[(.+?)]]/g;
  const matches = Array.from(sanitizedDescription.matchAll(regex));
  const type = matches?.[0]?.[1];

  return (
    <>
      <div className={
        classNames(
          'text-2xs text-grey-35',
          {
            'flex gap-2xs items-center': isCheckable,
            'text-red-500':              !!errors?.[name] && !formData?.[name],
          },
          textAlign,
        )}
      data-testid={TEST_ID.PREFIX + 'terms'}
      >
        {loading
          ? <Skeleton className="items-center justify-self-center w-[300px]" />
          : <div className="flex flex-col">
              {title ?
                  <div
                    className="inline-block pb-3xs font-semibold text-purple-0 text-font-xs">
                    {`${widgetTitle ? widgetTitle : null}${widgetTitle ? ':' : ''} ${title}`}
                  </div>
                : null
              }
              <div className="flex items-center justify-center gap-2xs">
                {isCheckable
                  ?
                    <>
                      <input
                        onClick={handleCheckboxChange}
                        type="checkbox"
                        checked={!!formData?.[name]}
                        className={classNames(
                          'relative w-md h-md shrink-0 appearance-none border-2 border-gray-300',
                          'rounded-sm bg-white checked:bg-purple-60 checked:border-purple-60',
                          'focus:outline-none focus:ring-purpleblue focus:ring-offset-0 focus:ring-0.5',
                          'cursor-pointer transition-colors after:absolute after:content-[\'\'] after:opacity-0',
                          'checked:after:opacity-100 after:top-[5px] after:left-3xs after:w-xs after:h-2xs',
                          'after:border-white after:border-l-4 after:border-b-4 after:rotate-[-45deg]',
                          { 'border-red-500': !!errors?.[name] },
                        )} />
                    </>
                  : null}
                <div className={classNames('inline-block', { 'cursor-pointer': isCheckable })}
                  onClick={handleCheckboxChange}
                  data-testid={TEST_ID.PREFIX + type}>
                  {parseText(description, buttons)}
                </div>
              </div>
            </div>
        }
      </div>
      <Drawer isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="flex flex-col h-full w-full p-md justify-between overflow-hidden">
          <div className="flex justify-between text-white pb-sm font-bold">
            <h1 className="text-h-lg">
              {terms?.title ?? ''}
            </h1>
            <span
              className="p-2xs cursor-pointer"
              onClick={() => setIsOpen(false)}
              data-testid="close-button"
            >
              <Close />
            </span>
          </div>
          {terms?.content
            ? <div className="rich-text text-white text-body-base overflow-y-auto" dangerouslySetInnerHTML={{
                __html: terms.content,
              }}
              />
            : <div className="text-white text-body-base overflow-y-auto">
                {termsError ? TERMS_UNAVAILABLE : ''}
              </div>}
        </div>
      </Drawer>
    </>
  );
};

const Memoized = memo(Consent);
Memoized.displayName = 'Consent';

export default Memoized;
